import { useMemo, useState } from 'react';
import { ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { Crown, Search, Truck } from 'lucide-react-native';

import { Badge, Card, EmptyState, SkeletonRows, StatTile, TextField } from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { productosVentaQuery } from '@/services/firebase/inventario';
import { proveedoresQuery } from '@/services/firebase/proveedores';
import { colors, radius, typography } from '@/theme/tokens';
import { formatBs } from '@/utils/format';
import type { ProductoVenta, Proveedor } from '@/types/domain';

interface FilaComparativa {
  nombre: string;
  presentacion: string;
  precios: Map<string, { precioCompra: number; precioVenta: number }>;
  precioMin: number | null;
  precioMax: number | null;
  ahorro: number;
  proveedorMejorId: string | null;
}

export function ComparativaPrecios() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const productos = useFirestoreCollection(productosVentaQuery);
  const proveedores = useFirestoreCollection(proveedoresQuery);

  const [search, setSearch] = useState('');
  const [filtro, setFiltro] = useState<'todos' | 'ahorro' | 'unico'>('todos');

  const filas = useMemo<FilaComparativa[]>(() => {
    if (productos.loading || proveedores.loading) return [];
    const grupos = new Map<string, { nombre: string; presentacion: string; items: ProductoVenta[] }>();
    productos.data.forEach((p) => {
      if (!p.proveedorId) return;
      const key = `${p.nombre.trim().toLowerCase()}|${p.presentacion.trim().toLowerCase()}`;
      const g = grupos.get(key);
      if (g) g.items.push(p);
      else grupos.set(key, { nombre: p.nombre, presentacion: p.presentacion, items: [p] });
    });

    return Array.from(grupos.values()).map<FilaComparativa>((g) => {
      const precios = new Map<string, { precioCompra: number; precioVenta: number }>();
      g.items.forEach((it) => {
        if (it.proveedorId) precios.set(it.proveedorId, { precioCompra: it.precioCompra ?? 0, precioVenta: it.precioVenta ?? 0 });
      });
      const valores = [...precios.values()].map((v) => v.precioCompra).filter((v) => v > 0);
      const min = valores.length ? Math.min(...valores) : null;
      const max = valores.length ? Math.max(...valores) : null;
      const ahorro = min !== null && max !== null ? max - min : 0;
      const mejorEntry = [...precios.entries()].find(([, v]) => v.precioCompra === min);
      return {
        nombre: g.nombre,
        presentacion: g.presentacion,
        precios,
        precioMin: min,
        precioMax: max,
        ahorro,
        proveedorMejorId: mejorEntry?.[0] ?? null,
      };
    });
  }, [productos, proveedores]);

  const filtered = useMemo(() => {
    let arr = filas;
    if (filtro === 'ahorro') arr = arr.filter((f) => f.ahorro > 0);
    else if (filtro === 'unico') arr = arr.filter((f) => f.precios.size === 1);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((f) => f.nombre.toLowerCase().includes(q));
    }
    return arr.sort((a, b) => {
      if (filtro === 'ahorro') return b.ahorro - a.ahorro;
      return a.nombre.localeCompare(b.nombre);
    });
  }, [filas, filtro, search]);

  const ahorroTotal = filas.reduce((sum, f) => sum + f.ahorro, 0);
  const conMultiples = filas.filter((f) => f.precios.size > 1).length;

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 18, paddingBottom: 80 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <StatTile
          label="Productos comparados"
          value={String(filas.length)}
          accent="magenta"
          icon={<Truck size={22} color={colors.magenta[700]} />}
          active={filtro === 'todos'}
          onPress={() => setFiltro('todos')}
        />
        <StatTile
          label="Con varios proveedores"
          value={String(conMultiples)}
          accent="success"
          hint="Comparables"
          active={filtro === 'ahorro'}
          onPress={() => setFiltro('ahorro')}
        />
        <StatTile
          label="Un solo proveedor"
          value={String(filas.length - conMultiples)}
          accent="neutral"
          hint="Sin alternativa"
          active={filtro === 'unico'}
          onPress={() => setFiltro('unico')}
        />
        <StatTile label="Ahorro potencial total" value={formatBs(ahorroTotal)} accent="success" hint="Si comprás siempre al más barato" />
      </View>

      <Card>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            backgroundColor: colors.crema[50],
            borderRadius: radius.full,
            paddingHorizontal: 14,
            height: 42,
            borderWidth: 1,
            borderColor: colors.ink[100],
          }}
        >
          <Search size={16} color={colors.ink[400]} />
          <TextField
            placeholder="Buscar producto…"
            value={search}
            onChangeText={setSearch}
            containerStyle={{ flex: 1 }}
          />
        </View>
      </Card>

      {productos.loading || proveedores.loading ? (
        <SkeletonRows rows={5} />
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState
            illustration="note"
            title="Sin datos para comparar"
            message={
              proveedores.data.length === 0
                ? 'Cargá proveedores y asignalos a los productos para ver la comparativa.'
                : 'No hay productos en esta selección.'
            }
          />
        </Card>
      ) : (
        <Card padding={0} style={{ overflow: 'hidden' }}>
          <Tabla filas={filtered} proveedores={proveedores.data} />
        </Card>
      )}
    </ScrollView>
  );
}

function Tabla({ filas, proveedores }: { filas: FilaComparativa[]; proveedores: Proveedor[] }) {
  return (
    <View>
      {/* Header */}
      <View
        style={{
          flexDirection: 'row',
          paddingVertical: 14,
          paddingHorizontal: 18,
          borderBottomWidth: 1,
          borderBottomColor: colors.ink[100],
          backgroundColor: colors.crema[50],
        }}
      >
        <Text
          style={{
            flex: 2,
            fontSize: 11,
            fontWeight: typography.weights.bold,
            color: colors.ink[500],
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}
        >
          Producto
        </Text>
        {proveedores.map((p) => (
          <Text
            key={p.id}
            style={{
              flex: 1,
              fontSize: 11,
              fontWeight: typography.weights.bold,
              color: colors.ink[500],
              letterSpacing: 1,
              textTransform: 'uppercase',
              textAlign: 'right',
            }}
            numberOfLines={1}
          >
            {p.nombre}
          </Text>
        ))}
        <Text
          style={{
            width: 100,
            fontSize: 11,
            fontWeight: typography.weights.bold,
            color: colors.ink[500],
            letterSpacing: 1,
            textTransform: 'uppercase',
            textAlign: 'right',
          }}
        >
          Ahorro
        </Text>
      </View>

      {/* Filas */}
      {filas.map((f, idx) => (
        <View
          key={`${f.nombre}-${idx}`}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: 14,
            paddingHorizontal: 18,
            borderBottomWidth: idx === filas.length - 1 ? 0 : 1,
            borderBottomColor: colors.ink[100],
            backgroundColor: idx % 2 === 0 ? colors.white : 'rgba(250,247,244,0.4)',
          }}
        >
          <View style={{ flex: 2, gap: 2 }}>
            <Text style={{ fontSize: 14, fontWeight: typography.weights.bold, color: colors.ink[900] }} numberOfLines={1}>
              {f.nombre}
            </Text>
            <Text style={{ fontSize: 11, color: colors.ink[500] }} numberOfLines={1}>
              {f.presentacion}
            </Text>
          </View>
          {proveedores.map((p) => {
            const valor = f.precios.get(p.id);
            const esMejor = f.proveedorMejorId === p.id;
            const tieneVarios = f.precios.size > 1;
            return (
              <View
                key={p.id}
                style={{
                  flex: 1,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 4,
                }}
              >
                {valor ? (
                  <>
                    {esMejor && tieneVarios ? <Crown size={12} color={colors.success[600]} /> : null}
                    <Text
                      style={{
                        fontFamily: typography.display.fontFamily,
                        fontSize: 14,
                        fontWeight: typography.weights.bold,
                        color: esMejor && tieneVarios ? colors.success[600] : colors.ink[800],
                      }}
                      numberOfLines={1}
                    >
                      {formatBs(valor.precioCompra)}
                    </Text>
                  </>
                ) : (
                  <Text style={{ color: colors.ink[300], fontSize: 14 }}>—</Text>
                )}
              </View>
            );
          })}
          <View style={{ width: 100, alignItems: 'flex-end' }}>
            {f.ahorro > 0 ? (
              <Badge label={`+${formatBs(f.ahorro)}`} tone="success" />
            ) : (
              <Text style={{ color: colors.ink[300], fontSize: 12 }}>—</Text>
            )}
          </View>
        </View>
      ))}
    </View>
  );
}
